import { LESSOR_DEFAULTS, CONTRACT_TYPES, INSPECTION_UNITS, GROUP_PREFIX_CONTRACT_TYPE } from './constants';
import { sanitizeTermsHtml } from './sanitizeTerms';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TITLE_PREFIXES = ['นางสาว', 'น.ส.', 'นาย', 'นาง', 'คุณ', 'ดร.', 'Mr.', 'Mrs.', 'Ms.', 'Miss', 'Mr', 'Mrs', 'Ms'];

const todayIso = () => new Date().toISOString().slice(0, 10);
const round2 = (n) => Math.round(n * 100) / 100;
const num = (v) => Number(v) || 0;
const newId = () => Math.random().toString(36).slice(2, 10);

// ── Form state ───────────────────────────────────────────────────────────────
// Only the fields the rental form (step 1) doesn't already collect. Customer
// name/address/phone, contact person, contract dates/duration and the item
// list are reused from step 1 / the Venio SO at send time instead of being
// asked for twice.
export const emptyEconForm = () => ({
  poa_number: '',
  department_code: '',
  contract_type: '',
  document_language: 'th',
  title: '',
  subject: '',
  purpose_text: '',
  contract_date: todayIso(),
  installation_location_name: '',
  customer_signer_job_position: '',
  ...LESSOR_DEFAULTS,
  rental_start_date: '',
  rental_asset_value: '',
  rental_amount_per_month: '',
  rental_vat_percent: '7',
  rental_advance_payment_schedule: 1,
  rental_first_payment_date: '',
  transport_amount: '',
  transport_vat_percent: '7',
  deposit_amount: '',
  deposit_vat_percent: '7',
  inspection_interval: '',
  inspection_interval_unit: 'month_per_time',
  inspection_interval_is_text: false,
  inspection_interval_text: '',
  additional_terms: ''
});

// Keys restored from a previously submitted payload (those are named exactly
// like the API fields, so the saved body maps straight back onto the form).
const RESTORABLE_KEYS = [
  'poa_number', 'department_code', 'contract_type', 'document_language',
  'title', 'subject', 'purpose_text', 'contract_date',
  'installation_location_name', 'customer_signer_job_position',
  'lessor_company_name', 'lessor_company_address', 'lessor_company_phone_number',
  'lessor_signer_full_name', 'lessor_signer_job_position',
  'rental_start_date', 'rental_asset_value', 'rental_amount_per_month', 'rental_vat_percent',
  'rental_advance_payment_schedule', 'rental_first_payment_date',
  'transport_amount', 'transport_vat_percent', 'deposit_amount', 'deposit_vat_percent',
  'inspection_interval', 'inspection_interval_unit', 'inspection_interval_is_text', 'inspection_interval_text',
  'additional_terms'
];

// ── Names / addresses ────────────────────────────────────────────────────────
export function splitName(full) {
  let parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1 && TITLE_PREFIXES.includes(parts[0])) parts = parts.slice(1);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

export function composeInstallationAddress(formData) {
  return [
    formData.location_address_line1,
    formData.location_address_line2,
    formData.location_subdistrict && `ตำบล/แขวง${formData.location_subdistrict}`,
    formData.location_district && `อำเภอ/เขต${formData.location_district}`,
    formData.location_province && `จังหวัด${formData.location_province}`,
    formData.location_postal_code
  ].filter(Boolean).join(' ');
}

// ── Approval chain (loa_detail) ──────────────────────────────────────────────
export const newPerson = (over = {}) => ({
  first_name: '', last_name: '', email: '', job_position: '', phone_number: '', ...over
});

export function newLoaLevel(party = 'lessor', userType = 'user') {
  return {
    id: newId(),
    contract_party_type: party,
    user_type: userType,
    signature_required: true,
    min_approve_count: 1,
    members: userType === 'user' ? [{ email: '' }] : [newPerson()]
  };
}

/** Lessee contact details collected in step 1, shaped as a "none user" member. */
export function lesseeFromContact(formData, econForm) {
  const { first_name, last_name } = splitName(formData.contact_name);
  return newPerson({
    first_name,
    last_name,
    email: formData.contact_email || '',
    job_position: econForm?.customer_signer_job_position || '',
    phone_number: String(formData.contact_phone || '').replace(/\D/g, '').slice(0, 10)
  });
}

export function defaultLoaLevels(formData, econForm) {
  const lessee = newLoaLevel('lessee', 'none user');
  lessee.members = [lesseeFromContact(formData, econForm)];
  return [newLoaLevel('lessor', 'user'), lessee];
}

export function loaFromApi(loa, fallbackLevels) {
  if (!loa || !Array.isArray(loa.levels) || loa.levels.length === 0) return fallbackLevels;
  return loa.levels.map(level => ({
    id: newId(),
    contract_party_type: level.contract_party_type || 'lessor',
    user_type: level.user_type || 'user',
    signature_required: level.signature_required !== false,
    min_approve_count: level.min_approve_count || 1,
    members: (level.members || []).map(m =>
      level.user_type === 'none user' ? newPerson(m) : { email: typeof m === 'string' ? m : m?.email || '' }
    )
  }));
}

export function loaToApi(levels) {
  return {
    levels: levels.map(level => ({
      min_approve_count: parseInt(level.min_approve_count, 10) || 1,
      signature_required: Boolean(level.signature_required),
      user_type: level.user_type,
      contract_party_type: level.contract_party_type,
      members: level.members.map(m => (
        level.user_type === 'user'
          ? String(m.email || '').trim()
          : {
            first_name: String(m.first_name || '').trim(),
            last_name: String(m.last_name || '').trim(),
            email: String(m.email || '').trim(),
            job_position: String(m.job_position || '').trim(),
            phone_number: String(m.phone_number || '').replace(/\D/g, '').slice(0, 10)
          }
      ))
    }))
  };
}

// ── Items (details[]) ────────────────────────────────────────────────────────
// ── Rental lines of an SO ────────────────────────────────────────────────────
// An SO can mix rental machines with ordinary goods. Only the rental lines belong on
// the contract (and decide its template). Same rule as the webhook: a product group
// ending "-R.." (KT-R01, HK-R01), a name starting "เช่า"/"ค่าเช่า", or a code that is
// an R-prefixed rental code (R3010202140005).
export const isRentalLine = (item) => {
  const group = String(item?.groupName || item?.stockGroup || '');
  return /-R\d*$/i.test(group)
    || /^\s*(ค่า)?เช่า/.test(String(item?.productName || ''))
    || /^R\d/.test(String(item?.productCode || ''));
};

/** The SO's rental lines (all lines if none can be recognised, so the form never goes empty). */
export function rentalLines(soData) {
  const all = soData?.data?.salesorderDetails || [];
  const rental = all.filter(isRentalLine);
  return rental.length > 0 ? rental : all;
}

/** Totals for the rental lines only; falls back to the SO's own totals when every line is rental. */
export function rentalTotals(soData) {
  const d = soData?.data || {};
  const all = d.salesorderDetails || [];
  const lines = rentalLines(soData);
  if (lines.length === all.length) {
    return { sub: d.subTotalWithDiscount ?? d.subTotal ?? 0, vat: d.totalVat ?? 0, grand: d.grandTotal ?? 0, excluded: 0 };
  }
  const sub = lines.reduce((sum, i) => sum + Number(i.totalPrice ?? Number(i.price) * Number(i.qty)), 0);
  const rate = Number(lines[0]?.vatTaxValue ?? 7);
  const vat = d.vatCalculation === false ? (sub * rate) / (100 + rate) : (sub * rate) / 100;
  return { sub: d.vatCalculation === false ? sub - vat : sub, vat, grand: d.vatCalculation === false ? sub : sub + vat, excluded: all.length - lines.length };
}

/**
 * Default "มูลค่าสินทรัพย์ให้เช่า": the central price (ราคากลาง) of each rented machine
 * times its quantity, summed. Empty when the SO carries no central price.
 */
export function centralAssetValue(soData) {
  const priced = rentalLines(soData).filter(i => i.centralPrice !== undefined && i.centralPrice !== null);
  if (priced.length === 0) return '';
  const total = priced.reduce((sum, i) => sum + Number(i.centralPrice) * (Number(i.qty) || 1), 0);
  return total > 0 ? String(round2(total)) : '';
}

// "ค่าเช่าเครื่อง NSS - เครื่องซักพรม ( 7 วัน)" -> "เครื่อง NSS - เครื่องซักพรม"
export function machineNameFromSo(soData) {
  const first = rentalLines(soData)[0];
  return String(first?.productName || '')
    .replace(/^\s*ค่าเช่า\s*/, '')
    .replace(/\s*\(\s*\d+\s*วัน\s*\)\s*$/, '')
    .trim();
}

export function defaultContractTitle(soData) {
  const machine = machineNameFromSo(soData);
  if (!machine) return '';
  return `สัญญาเช่า${machine.startsWith('เครื่อง') ? '' : ' '}${machine}`;
}

// What the machine is used for, by template — the usual answer, still editable.
export const PURPOSE_BY_TYPE = { 'LCT-001': 'ประกอบอาหาร', 'LCT-002': 'ทำความสะอาดพื้นที่' };
export const PURPOSE_SUGGESTIONS = ['ประกอบอาหาร', 'ทำความสะอาดพื้นที่', 'ล้างภาชนะ', 'ซักรีด'];
const PRODUCT_TYPE_BY_GROUP = { KT: 'เครื่องครัว', HK: 'เครื่องขัด/ล้างพื้น' };

export const newDetail = (over = {}) => ({
  product_type: '', description: '', serial_number: '', quantity: 1, unit: 'UNIT', remark: '', ...over
});

export function detailsFromSo(soData, savedDetails) {
  const saved = Array.isArray(savedDetails) ? savedDetails : [];
  // A previous send is the source of truth (staff may have added/removed rows).
  if (saved.length > 0) {
    return saved.map(d => newDetail({ ...d, serial_number: d.serial_number || '' }));
  }
  return rentalLines(soData).map(item =>
    newDetail({
      description: item.productName || '',
      quantity: item.qty || 1,
      product_type: PRODUCT_TYPE_BY_GROUP[String(item.stockGroup || item.groupName || '').slice(0, 2).toUpperCase()] || ''
    })
  );
}

// ── Money ────────────────────────────────────────────────────────────────────
const vat = (amount, percent) => (String(percent) === '7' ? round2(num(amount) * 0.07) : 0);

/** Local estimate only — e-Contract calculates the authoritative figures. */
export function computeAmounts(econForm) {
  const rentalVat = vat(econForm.rental_amount_per_month, econForm.rental_vat_percent);
  const transportVat = vat(econForm.transport_amount, econForm.transport_vat_percent);
  const depositVat = vat(econForm.deposit_amount, econForm.deposit_vat_percent);
  return {
    rentalVat,
    totalRental: round2(num(econForm.rental_amount_per_month) + rentalVat),
    transportVat,
    totalTransport: round2(num(econForm.transport_amount) + transportVat),
    depositVat,
    totalDeposit: round2(num(econForm.deposit_amount) + depositVat)
  };
}

/**
 * Last day of a term that starts on `iso` and runs `months` months — i.e. the
 * day BEFORE the anniversary (1 Oct 2026 + 12 months -> 30 Sep 2027). That is
 * how e-Contract derives rental_end_date, and the spec's own example (start
 * 1 Sep 2026, 12 months, end 31 Aug 2027) follows the same rule. The day is
 * clamped to the target month's length (31 Jan + 1 month -> 28 Feb, minus a day).
 */
export function addMonthsIso(iso, months) {
  const n = parseInt(months, 10);
  if (!iso || !n) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const total = (m - 1) + n;
  const targetYear = y + Math.floor(total / 12);
  const targetMonth = ((total % 12) + 12) % 12;
  const lastDayOfTarget = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  const end = new Date(Date.UTC(targetYear, targetMonth, Math.min(d, lastDayOfTarget)));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

// ── Typed-number helpers (text inputs, so the mouse wheel / arrow keys can't
// silently change a value) ────────────────────────────────────────────────────
/** Keeps digits and one dot, at most 2 decimals: "1,2a3.456.7" -> "123.45". */
export function sanitizeDecimal(raw) {
  const cleaned = String(raw ?? '').replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  return `${cleaned.slice(0, dot)}.${cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`;
}

/** "1234567.5" -> "1,234,567.5" (a trailing "." is kept while typing). */
export function withCommas(raw) {
  const value = String(raw ?? '');
  if (value === '') return '';
  const [int, dec] = value.split('.');
  const grouped = (int || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec === undefined ? grouped : `${grouped}.${dec}`;
}

export const sanitizeDigits = (raw) => String(raw ?? '').replace(/\D/g, '');

export const formatBaht = (n) =>
  Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Build / restore ──────────────────────────────────────────────────────────
/**
 * Pure — used both for the live preview and the actual send, so what staff
 * review before sending is exactly what gets sent.
 */
export function buildEconPayload({ econForm, loaLevels, details, formData, soData }) {
  const digits = (v) => String(v || '').replace(/\D/g, '');
  const addr = soData?.data?.customerAddress || {};
  const customerName = formData.customer_name || soData?.data?.customerName || '';
  const customerAddress =
    [addr.address1, addr.address2, addr.province, addr.postcode].filter(Boolean).join(' ') || composeInstallationAddress(formData);

  const payload = {
    contract_type: econForm.contract_type,
    poa_number: econForm.poa_number,
    department_code: econForm.department_code,
    document_language: econForm.document_language,
    title: econForm.title,
    subject: econForm.subject,
    purpose_text: econForm.purpose_text,
    contract_date: econForm.contract_date || null,
    contract_start_date: formData.contract_start_date || null,
    contract_end_date: formData.contract_end_date || null,

    lessor_company_name: econForm.lessor_company_name,
    lessor_company_address: econForm.lessor_company_address,
    lessor_company_phone_number: digits(econForm.lessor_company_phone_number),
    lessor_signer_full_name: econForm.lessor_signer_full_name,
    lessor_signer_job_position: econForm.lessor_signer_job_position,

    // Reused from step 1 / the Venio SO — not re-asked here.
    customer_company_name: customerName,
    customer_company_address: customerAddress,
    customer_company_phone_number: digits(formData.contact_phone),
    customer_signer_full_name: formData.contact_name,
    customer_signer_job_position: econForm.customer_signer_job_position,
    installation_location_name: econForm.installation_location_name,
    installation_address: composeInstallationAddress(formData),

    rental_asset_value: econForm.rental_asset_value,
    rental_period: formData.contract_duration,
    rental_start_date: econForm.rental_start_date || formData.contract_start_date || null,
    rental_advance_payment_schedule: econForm.rental_advance_payment_schedule,
    rental_first_payment_date: econForm.rental_first_payment_date || null,
    rental_amount_per_month: econForm.rental_amount_per_month,
    rental_vat_percent: econForm.rental_vat_percent,
    transport_amount: econForm.transport_amount,
    transport_vat_percent: econForm.transport_vat_percent,
    deposit_amount: econForm.deposit_amount,
    deposit_vat_percent: econForm.deposit_vat_percent,

    inspection_interval_is_text: Boolean(econForm.inspection_interval_is_text),
    additional_terms: sanitizeTermsHtml(econForm.additional_terms),
    loa_detail: loaToApi(loaLevels),
    details: details.map(d => ({
      product_type: d.product_type,
      description: d.description,
      serial_number: d.serial_number,
      quantity: d.quantity,
      unit: d.unit,
      remark: d.remark
    }))
  };

  if (econForm.inspection_interval_is_text) {
    payload.inspection_interval_text = econForm.inspection_interval_text;
  } else {
    payload.inspection_interval = econForm.inspection_interval;
    payload.inspection_interval_unit = econForm.inspection_interval_unit;
  }

  return payload;
}

/**
 * Picks the template from the SO's product groups. Returns
 * { type, group } or null when there is no mapped group, or the order mixes
 * families (then staff choose).
 */
export function suggestContractType(soData) {
  const found = new Map();
  rentalLines(soData).forEach(item => {
    const group = String(item.stockGroup || item.groupName || '').trim();
    const type = GROUP_PREFIX_CONTRACT_TYPE[group.slice(0, 2).toUpperCase()];
    if (type && !found.has(type)) found.set(type, group);
  });
  if (found.size !== 1) return null;
  const [[type, group]] = [...found.entries()];
  return { type, group };
}

/**
 * Rebuilds the form from whatever was submitted last (so a reload doesn't
 * leave contract_number blank while "update mode" has locked it), falling back
 * to sensible defaults taken from step 1.
 */
export function restoreEconState({ saved, formData, soData }) {
  const base = emptyEconForm();
  const customerName = formData.customer_name || soData?.data?.customerName || '';

  const title = defaultContractTitle(soData) || formData.contract_name || '';
  base.subject = title;
  base.title = title;
  base.installation_location_name = customerName;

  if (saved && typeof saved === 'object') {
    RESTORABLE_KEYS.forEach(key => {
      if (saved[key] !== undefined && saved[key] !== null) base[key] = saved[key];
    });
  }

  // Sends from before the API moved to two templates (LCT-001/LCT-002) used
  // other values — drop those so staff re-pick rather than send a value the
  // API now rejects.
  if (!CONTRACT_TYPES.some(t => t.value === base.contract_type)) base.contract_type = '';
  // Nothing chosen yet: take the template implied by the product group.
  if (!base.contract_type) {
    const suggestion = suggestContractType(soData);
    if (suggestion) base.contract_type = suggestion.type;
  }
  if (!base.purpose_text) base.purpose_text = PURPOSE_BY_TYPE[base.contract_type] || '';
  // Start the asset value from the machine's central price; a saved value (even edited) wins.
  if (!base.rental_asset_value) base.rental_asset_value = centralAssetValue(soData);
  // The SO's sales rep is almost always the one sending, so start from their department.
  if (!base.department_code && soData?.data?.staffDepartmentCode) base.department_code = soData.data.staffDepartmentCode;
  if (!INSPECTION_UNITS.some(u => u.value === base.inspection_interval_unit)) base.inspection_interval_unit = 'month_per_time';
  // The API only accepts a day of month, 1–31 (0 was the old default).
  const day = parseInt(base.rental_advance_payment_schedule, 10);
  if (!(day >= 1 && day <= 31)) base.rental_advance_payment_schedule = 1;

  const loaLevels = loaFromApi(saved?.loa_detail, defaultLoaLevels(formData, base));
  const details = detailsFromSo(soData, saved?.details);

  return { econForm: base, loaLevels, details };
}

// ── Validation (client side; the server re-validates everything) ─────────────
export function validateEcon({ econForm, loaLevels, details, formData }) {
  const errors = {};
  const required = {
    contract_type: 'กรุณาเลือกประเภทสัญญา',
    poa_number: 'กรุณาเลือกหนังสือมอบอำนาจ (POA)',
    department_code: 'กรุณากรอกรหัสแผนก',
    title: 'กรุณากรอกชื่อสัญญา (หัวเอกสาร)',
    purpose_text: 'กรุณากรอกวัตถุประสงค์การเช่า'
  };
  Object.entries(required).forEach(([field, message]) => {
    if (!String(econForm[field] || '').trim()) errors[field] = message;
  });

  const day = parseInt(econForm.rental_advance_payment_schedule, 10);
  if (!(day >= 1 && day <= 31)) errors.rental_advance_payment_schedule = 'ต้องเป็นวันที่ 1–31';

  if (String(formData.contact_phone || '').replace(/\D/g, '').length > 10) {
    errors.customer_company_phone_number = 'เบอร์โทรลูกค้าต้องไม่เกิน 10 หลัก (แก้ที่ขั้นตอนที่ 1)';
  }
  if (econForm.inspection_interval_is_text && !String(econForm.inspection_interval_text || '').trim()) {
    errors.inspection_interval_text = 'กรุณาระบุข้อความรอบการตรวจสอบ';
  }


  // Sample clauses leave ___ where a number must be chosen — never send those.
  if (/_{3,}/.test(String(econForm.additional_terms || '').replace(/<[^>]*>/g, ''))) {
    errors.additional_terms = 'ข้อตกลงเพิ่มเติมยังมีช่องว่าง ___ — กรุณากรอกตัวเลขหรือลบข้อนั้นออก';
  }

  const loaProblems = [];
  if (loaLevels.length === 0) loaProblems.push('ต้องมีผู้ลงนาม/ผู้อนุมัติอย่างน้อย 1 ลำดับ');
  loaLevels.forEach((level, i) => {
    const label = `ลำดับที่ ${i + 1}`;
    if (level.members.length === 0) loaProblems.push(`${label}: ต้องมีผู้อนุมัติอย่างน้อย 1 คน`);
    if (parseInt(level.min_approve_count, 10) > level.members.length) loaProblems.push(`${label}: จำนวนผู้อนุมัติขั้นต่ำมากกว่าจำนวนคน`);
    level.members.forEach((m, j) => {
      const who = `${label} คนที่ ${j + 1}`;
      if (!EMAIL_RE.test(String(m.email || '').trim())) loaProblems.push(`${who}: อีเมลไม่ถูกต้อง`);
      if (level.user_type === 'none user') {
        if (!String(m.first_name || '').trim()) loaProblems.push(`${who}: ต้องระบุชื่อ`);
        if (!String(m.last_name || '').trim()) loaProblems.push(`${who}: ต้องระบุนามสกุล`);
      }
    });
  });
  if (loaProblems.length > 0) errors.loa_detail = loaProblems.join(' · ');

  const detailProblems = [];
  details.forEach((d, i) => {
    const q = Number(d.quantity);
    if (!Number.isInteger(q) || q < 0) detailProblems.push(`รายการที่ ${i + 1}: จำนวนต้องเป็นเลขจำนวนเต็ม`);
  });
  if (detailProblems.length > 0) errors.details = detailProblems.join(' · ');

  return errors;
}

/**
 * The API reports validation as { field: ["message", ...] } and everything
 * about contract *state* as a single { detail: "..." } string.
 */
export function splitRemoteErrors(data) {
  const fieldErrors = {};
  const general = [];
  if (!data || typeof data !== 'object') return { fieldErrors, general };

  const flatten = (value) => {
    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) return value.flatMap(flatten);
    if (value && typeof value === 'object') return Object.entries(value).flatMap(([k, v]) => flatten(v).map(m => `${k}: ${m}`));
    return [];
  };

  Object.entries(data).forEach(([key, value]) => {
    if (key === 'detail') {
      general.push(...flatten(value));
    } else if (key.startsWith('_')) {
      // internal markers (e.g. _mock) — not an error
    } else {
      const messages = flatten(value);
      if (messages.length > 0) fieldErrors[key] = messages.join(' · ');
    }
  });
  return { fieldErrors, general };
}
